import type { UserRole } from "@/contexts/UserContext";
import { hasAnyOfRoles } from "@/contexts/UserContext";
import type { JSX } from "react";
import { LocationFilterDropdown } from "./LocationFilterDropdown";
import { RegionFilterDropdown } from "./RegionFilterDropdown";
import { ResponsiblePersonFilterDropdown } from "./ResponsiblePersonFilterDropdown";

type CampaignFilterKey =
  | "campaign_type_payed"
  | "campaign_status_live"
  | "campaign_type_test"
  | "campaign_status_paused"
  | "campaign_status_finished"
  | "campaign_status_draft"
  | "campaign_status_all"
  | "campaign_region"
  | "campaign_location"
  | "campaign_visibility_all"
  | "campaign_visibility_not_archived"
  | "campaign_visibility_archived"
  | "campaign_no_ad_campaigns_only"
  | "campaign_only_partners"
  | "responsible_person"
  | "campaign_perspective_connected"
  | "campaign_perspective_unconnected"
  | "campaign_funnel_perspective"
  | "campaign_funnel_funnelui";

interface FilterOptions {
  type: "checkbox" | "custom";
  buildComponent?: (props: {
    setValues: (values: string[]) => void;
    values: string[];
  }) => JSX.Element;
}

interface Filter<Key extends string> {
  key: Key;
  displayText: string;

  options?: FilterOptions;
  isParent?: boolean;
}

interface Section<T> {
  name: string;
  filters: T[];
  displayPredicate?: (roles: UserRole | null) => boolean;
}

const defaultCampaignFilterKeys: CampaignFilterKey[] = [
  "campaign_status_all",
  "campaign_type_payed",
  "campaign_type_test",
  "campaign_visibility_not_archived",
  "campaign_perspective_connected",
  "campaign_funnel_perspective",
  "campaign_funnel_funnelui",
];
const campaignFilterSections: Section<Filter<CampaignFilterKey>>[] = [
  {
    name: "Status",
    filters: [
      {
        key: "campaign_status_all",
        displayText: "Alle",
        isParent: true,
      },
      {
        key: "campaign_status_live",
        displayText: "Aktiv",
      },
      {
        key: "campaign_status_paused",
        displayText: "Pausiert",
      },
      {
        key: "campaign_status_finished",
        displayText: "Abgeschlossen",
      },
      {
        key: "campaign_status_draft",
        displayText: "Ausstehend",
      },
    ],
  },
  {
    name: "Kampagnentyp",
    filters: [
      {
        key: "campaign_perspective_connected",
        displayText: "Funnel-Kampagnen",
      },
      {
        key: "campaign_perspective_unconnected",
        displayText: "Traffic-Kampagnen",
      },
    ],
  },
  {
    name: "Funnel",
    filters: [
      {
        key: "campaign_funnel_perspective",
        displayText: "Perspective",
      },
      {
        key: "campaign_funnel_funnelui",
        displayText: "FunnelUI",
      },
    ],
  },
  {
    name: "Kategorie",
    filters: [
      {
        key: "campaign_type_payed",
        displayText: "Bezahlt",
      },
      {
        key: "campaign_type_test",
        displayText: "Test",
      },
    ],
  },
  {
    name: "Sichtbarkeit",
    displayPredicate: (role) => role != "customer",
    filters: [
      {
        key: "campaign_visibility_all",
        displayText: "Alle",
        isParent: true,
      },
      {
        key: "campaign_visibility_not_archived",
        displayText: "Nicht ausgeblendet",
      },
      {
        key: "campaign_visibility_archived",
        displayText: "Ausgeblendet",
      },
    ],
  },
  {
    name: "Standort & Region",
    filters: [
      {
        key: "campaign_region",
        displayText: "Region",
        options: {
          type: "custom",
          buildComponent: ({ setValues, values }) => (
            <RegionFilterDropdown setValues={setValues} values={values} />
          ),
        },
      },
      {
        key: "campaign_location",
        displayText: "Standort",
        options: {
          type: "custom",
          buildComponent: ({ setValues, values }) => (
            <LocationFilterDropdown setValues={setValues} values={values} />
          ),
        },
      },
    ],
  },
  {
    name: "Zuständige Person",
    filters: [
      {
        key: "responsible_person",
        displayText: "Zuständige Person",
        options: {
          type: "custom",
          buildComponent: ({ setValues, values }) => (
            <ResponsiblePersonFilterDropdown
              setValues={setValues}
              values={values}
            />
          ),
        },
      },
    ],
  },
  {
    name: "Sonstiges",
    displayPredicate: (role) =>
      hasAnyOfRoles(["everbay_admin", "employee"], role),
    filters: [
      {
        key: "campaign_no_ad_campaigns_only",
        displayText: "Nur ohne Ad-Kampagnen",
      },
      {
        key: "campaign_only_partners",
        displayText: "Nur Partner",
      },
    ],
  },
];

export {
  campaignFilterSections,
  defaultCampaignFilterKeys,
  type CampaignFilterKey,
  type Filter,
  type FilterOptions,
  type Section,
};
